import { AIConcept } from '@/types';
import { concepts as localConcepts } from '@/data/concepts';

// API endpoint - will be set via environment variable
const API_URL = import.meta.env.VITE_API_URL || '';

interface ConceptsResponse {
  concepts: Array<{
    id: string;
    name: string;
    fullName: string;
    description: string;
    category: string;
  }>;
  count: number;
}

// Track data source
let dataSource: 'api' | 'local' = 'local';

/**
 * Get current data source
 */
export function getDataSource(): 'api' | 'local' {
  return dataSource;
}

/**
 * Fetch concepts from API with fallback to local data
 */
export async function fetchConcepts(limit?: number): Promise<AIConcept[]> {
  // If no API URL, use local concepts
  if (!API_URL) {
    console.log('🟡 No API URL configured, using local concepts');
    dataSource = 'local';
    return shuffleArray([...localConcepts]).slice(0, limit || localConcepts.length);
  }

  try {
    const url = new URL(`${API_URL}/concepts`);
    if (limit) {
      url.searchParams.set('limit', limit.toString());
    }

    console.log('🔵 Fetching concepts from API:', url.toString());

    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data: ConceptsResponse = await response.json();
    
    console.log(`✅ Successfully fetched ${data.count} concepts from DynamoDB`);
    dataSource = 'api';
    
    // Transform API response to match frontend AIConcept interface
    return data.concepts.map(concept => ({
      name: concept.name,
      full: concept.fullName,
      desc: concept.description,
      category: concept.category,
    }));

  } catch (error) {
    console.error('❌ Failed to fetch from API, using local fallback:', error);
    dataSource = 'local';
    // Fallback to local concepts
    return shuffleArray([...localConcepts]).slice(0, limit || localConcepts.length);
  }
}

/**
 * Fetch concepts by category
 */
export async function fetchConceptsByCategory(category: string): Promise<AIConcept[]> {
  if (!API_URL) {
    return localConcepts.filter(c => c.category === category);
  }

  try {
    const url = new URL(`${API_URL}/concepts`);
    url.searchParams.set('category', category);

    const response = await fetch(url.toString());
    
    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const data: ConceptsResponse = await response.json();
    
    return data.concepts.map(concept => ({
      name: concept.name,
      full: concept.fullName,
      desc: concept.description,
      category: concept.category,
    }));

  } catch (error) {
    console.error('Failed to fetch by category, using local fallback:', error);
    return localConcepts.filter(c => c.category === category);
  }
}

/**
 * Get all unique categories
 */
export function getCategories(): string[] {
  const categories = new Set(localConcepts.map(c => c.category).filter((c): c is string => c !== undefined));
  return Array.from(categories).sort();
}

/**
 * Fisher-Yates shuffle algorithm
 */
function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}
